import {PhoneHQAvatar, PhoneHQIconAvatar, PhoneHQButton, PhoneHQTooltip, PhoneHQCheckbox, PhoneHQGlyph} from 'ch3-ui-lib';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {useLazyResolveUserIdsQuery, UserElement} from "../../store/features/Users/userService";
import {getUserStatus} from "../../utilis/getStatus.ts";
import {ChevronLeft, X, ChevronRight, ArrowLeft, ArrowRight, Settings, PencilLine, Lock} from "lucide-react";
import {Search} from "lucide-react";
import {hexToEmoji} from "../Emoji/emoji";
import {convertFromISOFormat} from "../../utilis/timeUtilis";
import {checkStatusExpiration} from "../../utilis/statusUtilis.ts";
import {useIdResolver} from "../../hooks/useIdResolver.ts";
import {useStatusSubscription} from "../../hooks/useStatusSubscription";
import googleCalendarIcon from "../../assets/google-calendar.svg";
import {EncryptedStatus} from "./EncryptedStatus.tsx";

export interface UserWithMatrixId extends UserElement {
    matrixId: string;
}

interface CreateRoomModalProps {
    users: UserElement[];
    onClose: () => void;
    onRoomCreate: (matrixIds: string[], roomName?: string) => void;
    isEditing?: boolean;
    startPreparingChat?: (users: UserWithMatrixId[], optionalGroupName?: string) => void;
}

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
                                                             users,
                                                             onClose,
                                                             onRoomCreate,
                                                             isEditing = false,
                                                             startPreparingChat
                                                         }) => {
    const [selectedUsers, setSelectedUsers] = useState<UserElement[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isGroup, setIsGroup] = useState(false);
    const [showSettings, setShowSettings] =useState(false)

    const [optionalGroupName, setOptionalGroupName] = useState('');

    const [scrollOffset, setScrollOffset] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const currentUserId = useSelector(selectUserId);
    const userIds = useMemo(() => users.map(user => user.id), [users]);
    const userIdsAsStrings = useMemo(() => userIds.map(id => String(id)), [userIds]);

    // Use Redux-based ID resolver
    const { getMatrixId, isLoading: isResolvingIds } = useIdResolver();
    const [resolveUserIds, { isFetching: isResolvingSelectedUserIds }] = useLazyResolveUserIdsQuery();

    const { statuses } = useStatusSubscription({
        userIds: userIdsAsStrings
    });

    const chatUsers = useMemo(() => {
        return users?.reduce((acc: any[], user: any) => {
            const status = statuses.find(status => status.userId === user.id);
            const isUninitialized = user.chatInitializedAt == null;
            if (user.id != currentUserId && (status || isUninitialized)) {
                acc.push({
                    ...status,
                    ...user,
                    userId: user.id,
                });
            }
            return acc;
        }, []);
    }, [users, statuses, currentUserId]);

    const filteredUsers = chatUsers.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();

        return Number(user.id) !== Number(currentUserId) &&
            (user.firstName.toLowerCase().includes(searchLower) ||
                user.lastName.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower) ||
                fullName.includes(searchLower));
    });

    const numberOfVisibleUsers = 6;

    const visibleUsers = selectedUsers.slice(scrollOffset, scrollOffset + numberOfVisibleUsers);
    const remainingCount = selectedUsers.length - (scrollOffset + numberOfVisibleUsers);
    const showRemainingBadge = remainingCount > 0;

    const canScrollLeft = scrollOffset > 0;
    const canScrollRight = scrollOffset + numberOfVisibleUsers < selectedUsers.length;

    const scrollLeft = () => {
        if (canScrollLeft) {
            setScrollOffset(Math.max(0, scrollOffset - 1));
        }
    };

    const scrollRight = () => {
        if (canScrollRight) {
            setScrollOffset(Math.min(selectedUsers.length - numberOfVisibleUsers, scrollOffset + 1));
        }
    };

    useEffect(() => {
        if (selectedUsers.length <= numberOfVisibleUsers) {
            setScrollOffset(0);
        } else if (scrollOffset + numberOfVisibleUsers > selectedUsers.length) {
            setScrollOffset(Math.max(0, selectedUsers.length - numberOfVisibleUsers));
        }
    }, [selectedUsers.length]);

    const isUserUninitialized = (user: UserElement) => user.chatInitializedAt == null;

    const uninitializedTooltip = "Can't invite yet — user hasn't completed their first-time setup.";

    const handleCheckboxChange = (user: UserElement) => {
        if (isUserUninitialized(user)) return;
        setSelectedUsers(prev => {
            const isAlreadySelected = prev.find(selected => selected.id === user.id);
            if (isAlreadySelected) {
                return prev.filter(selected => selected.id !== user.id);
            } else {
                return [...prev, user];
            }
        });
    };

    const getUserDisplayName = (user: UserElement) => {
        return `${user.firstName} ${user.lastName}`;
    };

    const isCustomStatusExpired = (user: any) => {
        if (!user.customUserStatus?.dueDate) return true;

        let isExpired = true;
        checkStatusExpiration(user, (expired: boolean) => {
            isExpired = expired;
        });
        return isExpired;
    };

    const getStatusDisplayInfo = (user: any) => {
        const hasCustomStatus = user.customUserStatus?.name && !isCustomStatusExpired(user);
        const hasCustomIcon = user.customUserStatus?.icon && !isCustomStatusExpired(user);
        const hasMeetingStatus = user.meetingUserStatus?.status === 'IN_MEETING';
        const meetingExpires = hasMeetingStatus
            ? (() => {
                const {dateString, timeString} = convertFromISOFormat(user.meetingUserStatus?.dateTo || '');
                return dateString ? `${dateString}, ${timeString}` : timeString;
            })()
            : '';
        const meetingStatusName = hasMeetingStatus
            ? `In a meeting${meetingExpires ? ` until ${meetingExpires}` : ''}`
            : null;
        const statusName = [
            hasCustomStatus ? user.customUserStatus.name : null,
            meetingStatusName
        ].filter(Boolean).join(' · ') || null;

        if (statusName) {
            const { dateString, timeString } = convertFromISOFormat(user.customUserStatus?.dueDate || '');
            const expires = dateString ? `${dateString}, ${timeString}` : timeString;

            return {
                hasStatus: true,
                hasMeetingStatus,
                statusName,
                statusIcon: hasCustomIcon ? user.customUserStatus.icon : null,
                expires: expires
            };
        }

        return {
            hasStatus: false,
            hasMeetingStatus: false,
            statusName: null,
            statusIcon: null,
            expires: null
        };
    };

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (showSettings && inputRef.current) {
            inputRef.current.focus();
        }
    }, [showSettings]);

    const resolveUsersWithMatrixIds = async (usersToResolve: UserElement[]): Promise<UserWithMatrixId[] | null> => {
        const unresolvedUserIds = usersToResolve
            .filter(user => !getMatrixId(String(user.id)))
            .map(user => String(user.id));
        let resolvedMatrixIds: Record<string, string> = {};

        if (unresolvedUserIds.length > 0) {
            try {
                resolvedMatrixIds = await resolveUserIds({ userIds: unresolvedUserIds }).unwrap();
            } catch (error) {
                console.error('Failed to resolve Matrix IDs for selected users', error);
                alert('Unable to create the group because user chat IDs could not be loaded. Please try again.');
                return null;
            }
        }

        const usersWithMatrixIds = usersToResolve.map(user => ({
            ...user,
            matrixId: getMatrixId(String(user.id)) || resolvedMatrixIds[String(user.id)] || ''
        }));
        const usersWithoutMatrixIds = usersWithMatrixIds.filter(user => !user.matrixId);

        if (usersWithoutMatrixIds.length > 0) {
            console.error('Some users do not have Matrix IDs', usersWithoutMatrixIds.map(user => user.id));
            alert('Unable to create the group because one or more selected users are not ready for chat.');
            return null;
        }

        return usersWithMatrixIds;
    };

    const handleCreateGroup = async () => {
        if(!startPreparingChat) return;

        const usersWithMatrixIds = await resolveUsersWithMatrixIds(selectedUsers);
        if (!usersWithMatrixIds) return;

        startPreparingChat(usersWithMatrixIds, optionalGroupName);
        onClose();
    };

    const handleSelectAll = () => {
        setSelectedUsers(chatUsers.filter((u: UserElement) => !isUserUninitialized(u)));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-surface rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-hidden p-1 mx-4 relative">

                <div className="flex items-center justify-between p-5">
                    <div className={'flex gap-3 items-center'}>
                        {isGroup && <ChevronLeft onClick={() => {
                            setIsGroup(false)
                            setShowSettings(false);
                            setOptionalGroupName('');
                        }} size={18}
                                                 className={'text-ink hover:bg-surface-muted rounded-md cursor-pointer'}/>}
                        {!isEditing ? <h2 className="text-base font-medium text-ink">{isGroup ? 'New Group' : 'New chat'}</h2> : <h2 className="text-base font-semibold text-ink">Add members</h2>}
                    </div>
                    <div className="flex items-center space-x-2">
                        {isGroup && <>
                            <button className={`${showSettings ? 'bg-surface-muted' : ''} rounded-md p-1 text-ink`} onClick={() => {
                                setOptionalGroupName('');
                                setShowSettings(prev => !prev)
                            }}>
                                <Settings className="p-1.5" size={32}/>
                            </button>
                            <button
                                onClick={handleSelectAll}
                                className="p-1 text-ink disabled:opacity-50 disabled:cursor-not-allowed transition-colors outline outline-line rounded-lg py-2 px-3">
                                Select all
                            </button>
                        </>
                        }
                    </div>
                    {!isGroup &&
                        <X onClick={onClose} className="text-ink cursor-pointer hover:bg-surface-muted rounded-md"
                           size={18}/>}
                </div>

                <div className="overflow-y-auto" style={{maxHeight: 'calc(90vh - 140px)', paddingBottom: '80px'}}>
                    <div
                        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                        style={{ gridTemplateRows: showSettings ? '1fr' : '0fr' }}
                    >
                        <div className="overflow-hidden min-h-0">
                            {isGroup && <div className="relative">
                                <div className={'flex items-center px-4 border-y border-line'}>
                                    <PencilLine size={32} className={'bg-surface-muted text-ink rounded-md p-2 m-1.5'} />
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={optionalGroupName}
                                        onChange={(e) => setOptionalGroupName(e.target.value)}
                                        placeholder="Optional group name"
                                        className="w-full text-ink placeholder:text-ink-muted bg-surface px-4 py-4 border-none focus:ring-none !ring-transparent focus:border-none outline-none text-tiny"
                                    />
                                </div>
                            </div>}
                            <div className={'px-5 py-1.5'}>
                                <EncryptedStatus isEncrypted={true}/>
                            </div>
                        </div>
                    </div>
                    {/* Selected User List with Scrolling */}
                    <div className={`flex flex-col ${selectedUsers.length > 0 ? 'border-b border-line' : ''}`}>
                        <div className={'flex items-center justify-between px-5 text-ink-muted text-tiny py-[6px]'}>
                            <span>Members {selectedUsers.length > 0 && `(${selectedUsers.length})`}</span>
                            {selectedUsers.length > numberOfVisibleUsers && (
                                <div className="flex items-center space-x-2">
                                    <ArrowLeft className={`cursor-pointer  ${!canScrollLeft ? '!text-ink-muted' : '!text-ink' }`}  onClick={scrollLeft} size={14}  />
                                    <ArrowRight className={`cursor-pointer ${!canScrollRight ? '!text-ink-muted' : '!text-ink' }`}  onClick={scrollRight} size={14}  />
                                </div>
                            )}
                        </div>


                        {selectedUsers.length > 0 && (
                            <div className="flex gap-2 p-2" ref={scrollContainerRef}>
                                {visibleUsers.map((user) => {
                                    const statusInfo = getStatusDisplayInfo(user);
                                    return (
                                        <div key={user.id} className="flex flex-col items-center px-3 py-1 rounded-lg min-w-0">
                                            <div className="relative">
                                                <PhoneHQAvatar src={user.avatarUrl} name={`${user.firstName} ${user.lastName}`} size="40px"/>
                                                {/* Custom status icon overlay */}
                                                {statusInfo.hasStatus && (statusInfo.statusIcon || statusInfo.hasMeetingStatus) && (
                                                    <div className="absolute -bottom-1 -right-1 bg-surface rounded-full w-6 h-6 flex items-center justify-center border border-line">
                                                        {statusInfo.statusIcon ? (
                                                            hexToEmoji(statusInfo.statusIcon, '!w-4 !h-4')
                                                        ) : (
                                                            <img className="h-4 w-4" src={googleCalendarIcon} alt="" />
                                                        )}
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => setSelectedUsers(prev => prev.filter(selected => selected.id !== user.id))}
                                                    className="absolute -top-1 -right-1 bg-surface-muted text-ink rounded-md w-5 h-5 flex items-center justify-center"
                                                >
                                                    <PhoneHQGlyph name="close" size={14}/>
                                                </button>
                                            </div>
                                            <span className="text-ink text-sm font-medium mt-2 text-center max-w-[60px]">
                                                {getUserDisplayName(user)}
                                            </span>
                                            {/* Status name display */}
                                            {statusInfo.hasStatus && (
                                                <PhoneHQTooltip content={statusInfo.statusName} side="top"><span className="block max-w-[80px] truncate text-center text-xs text-ink-muted mt-1">
                                                        {statusInfo.statusName}
                                                    </span></PhoneHQTooltip>
                                            )}
                                        </div>
                                    );
                                })}
                                {showRemainingBadge && (
                                    <div className="flex flex-col items-center justify-center px-3 py-1 rounded-lg min-w-0">
                                        <div className="relative flex items-center justify-center">
                                            <div className="px-3 py-2 bg-surface-muted rounded-xl flex items-center justify-center text-tiny">
                                                <span className="text-ink text-sm font-medium">+{remainingCount}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* User Selection */}
                    <div>
                        {/* Search */}
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search name"
                                className="w-full text-ink placeholder:text-ink-muted bg-surface px-4 py-4 pl-[78px] border-none border-y !border-y-line focus:border-line focus:ring-none focus:ring-none"
                            />
                            <Search size={18} className={' absolute left-[26px] top-5'} />
                        </div>
                        {!isGroup && !isEditing && <div onClick={() => {
                            setIsGroup(true)
                            setShowSettings(true);
                        }}
                                                        className={' rounded-xl flex items-center cursor-pointer hover:bg-surface-muted justify-between py-2 px-4'}>
                            <div className={'flex items-center gap-4'}>
                                <PhoneHQIconAvatar icon='group' color='zinc' size="40px"/>
                                <span className="text-ink text-sm font-semibold ml-2">New group</span>
                            </div>

                            <ChevronRight onClick={() => {
                                setIsGroup(true);
                                setShowSettings(true);
                            }} size={18}
                                          className={'text-ink hover:bg-surface-muted rounded-md cursor-pointer'}/>
                        </div>
                        }

                        {/* User List */}
                        <div className="rounded-lg max-h-64 py-4 border-t border-line">
                            {filteredUsers.length === 0 ? (
                                <div className="p-4 text-center text-ink-muted">
                                    {searchTerm ? 'No users found matching your search' : 'No users available'}
                                </div>
                            ) : (
                                <div className={'pb-12'}>
                                    {filteredUsers.map((user) => {
                                        const statusInfo = getStatusDisplayInfo(user);
                                        const uninitialized = isUserUninitialized(user);
                                        return (
                                            <div key={user.userId || user.id}
                                                 onClick={() => {
                                                     if (uninitialized) return;
                                                     if (isGroup || isEditing) {
                                                         handleCheckboxChange(user);
                                                     } else {
                                                         if(startPreparingChat) {
                                                             const matrixId = getMatrixId(String(user.id));
                                                             if (matrixId) {
                                                                 startPreparingChat([{ ...user, matrixId }]);
                                                                 onClose();
                                                             } else {
                                                                 console.error('Matrix ID not resolved for user:', user.id, 'Still resolving:', isResolvingIds);
                                                                if (!isResolvingIds) {
                                                                    alert(`Unable to start chat. Please try again.`);
                                                                    onClose();
                                                                }
                                                                // Don't close if still resolving - user can try again
                                                             }
                                                         } else {
                                                             onClose();
                                                         }
                                                     }
                                                 }}
                                                 className={`p-4 transition-colors flex justify-between rounded-lg ${uninitialized ? 'opacity-50 cursor-not-allowed' : 'hover:bg-surface-muted cursor-pointer'}`}>
                                                <label className={`flex items-center space-x-3 flex-1 ${uninitialized ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                                        <div className="flex min-w-0 flex-1 items-center space-x-3">
                                                        <div className="relative">
                                                            <PhoneHQAvatar src={user.avatarUrl} name={user.firstName + ' ' + user.lastName} status={getUserStatus(user)} size="40px"/>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-ink truncate">
                                                                {getUserDisplayName(user)}
                                                            </p>
                                                        </div>
                                                        {statusInfo.hasStatus && (
                                                            <PhoneHQTooltip content={statusInfo.statusName} side="top"><div className="flex min-w-0 max-w-[320px] items-center gap-1 text-tiny text-ink-muted">
                                                                    {statusInfo.statusIcon ? (
                                                                        <div className="w-7 shrink-0">
                                                                            {hexToEmoji(statusInfo.statusIcon, '!w-4 !h-4')}
                                                                        </div>
                                                                    ) : statusInfo.hasMeetingStatus ? (
                                                                        <div className="flex w-7 shrink-0 justify-center">
                                                                            <img className="h-4 w-4" src={googleCalendarIcon} alt="" />
                                                                        </div>
                                                                    ) : null}
                                                                    <span className="min-w-0 flex-1 truncate">
                                                                        {statusInfo.statusName}
                                                                    </span>
                                                                </div></PhoneHQTooltip>
                                                        )}
                                                    </div>
                                                </label>
                                                {uninitialized ? (
                                                    <div
                                                        className={'flex items-center text-ink-muted'}
                                                        data-tooltip-left={uninitializedTooltip}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Lock size={16} />
                                                    </div>
                                                ) : (isGroup || isEditing) &&
                                                    <div className={'flex items-center'} onClick={(e) => e.stopPropagation()}>
                                                        <PhoneHQCheckbox
                                                            theme="light"
                                                            checked={selectedUsers.some(selected => selected.id === user.id)}
                                                            onCheckedChange={() => handleCheckboxChange(user)}
                                                        />
                                                    </div>
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Floating Button at Bottom */}
                {isEditing && <div className="absolute flex bottom-0 left-0 right-0 p-4 from-black via-black/90 to-transparent">
                    <PhoneHQButton
                        variant="primary"
                        className="w-full max-w-xl mx-auto"
                        onClick={() => {
                            const matrixIds = selectedUsers
                                .map(user => getMatrixId(String(user.id)))
                                .filter((id): id is string => !!id);

                            if (matrixIds.length !== selectedUsers.length) {
                                console.error('Some users do not have Matrix IDs');
                                // TODO: Show error toast to user
                            }

                            onRoomCreate(matrixIds);
                            onClose();
                        }}>
                        Add members
                    </PhoneHQButton>
                </div>}
                {isGroup && selectedUsers.length > 0 &&
                    <div className="absolute flex bottom-0 left-0 right-0 p-4 from-black via-black/90 to-transparent">
                        <PhoneHQButton
                            variant="primary"
                            className="w-full max-w-xl mx-auto"
                            disabled={isResolvingSelectedUserIds}
                            onClick={handleCreateGroup}>
                            {isResolvingSelectedUserIds ? 'Preparing…' : 'Create'}
                        </PhoneHQButton>
                    </div>}
            </div>
        </div>
    );
};

export default CreateRoomModal;
